import React, { useState, useEffect } from 'react';
import { Zap, Check, Plus, Trash2 } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { Button } from '@shared/components/ui/Button';
import { LLMParameters, SYSTEM_PRESETS } from '../utils/llmParameterHelpers';

interface Preset {
  id: string;
  name: string;
  description: string | null;
  parameters: LLMParameters;
  use_case: string | null;
  is_system_preset: boolean;
}

interface ParameterPresetSelectorProps {
  onSelectPreset: (parameters: LLMParameters) => void;
  currentParameters: LLMParameters;
  useCase?: string;
}

export const ParameterPresetSelector: React.FC<ParameterPresetSelectorProps> = ({
  onSelectPreset,
  currentParameters,
  useCase = 'general'
}) => {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('llm_parameter_presets')
        .select('*')
        .order('is_system_preset', { ascending: false })
        .order('name', { ascending: true });

      if (error) throw error;
      setPresets(data || []);
    } catch (error: any) {
      console.error('Error loading presets:', error);
      toast.error('Failed to load parameter presets');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPreset = (preset: Preset) => {
    setSelectedPresetId(preset.id);
    onSelectPreset(preset.parameters);
    toast.success(`Applied preset: ${preset.name}`);
  };

  const handleDeletePreset = async (presetId: string, isSystem: boolean) => {
    if (isSystem) {
      toast.error('Cannot delete system presets');
      return;
    }

    if (!confirm('Are you sure you want to delete this preset?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('llm_parameter_presets')
        .delete()
        .eq('id', presetId);

      if (error) throw error;

      toast.success('Preset deleted successfully');
      loadPresets();
    } catch (error: any) {
      console.error('Error deleting preset:', error);
      toast.error('Failed to delete preset');
    }
  };

  const isPresetSelected = (preset: Preset): boolean => {
    return JSON.stringify(preset.parameters) === JSON.stringify(currentParameters);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">Parameter Presets</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => toast.info('Custom presets coming soon!')}
          className="flex items-center gap-1"
        >
          <Plus className="w-3 h-3" />
          Save as Preset
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {presets.map((preset) => {
          const isSelected = isPresetSelected(preset);
          const isCurrent = preset.id === selectedPresetId;

          return (
            <button
              key={preset.id}
              onClick={() => handleSelectPreset(preset)}
              className={`
                relative p-4 rounded-lg border-2 text-left transition-all
                ${isSelected || isCurrent
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300 bg-white'
                }
              `}
            >
              {/* System preset indicator */}
              {preset.is_system_preset && (
                <div className="absolute top-2 right-2">
                  <Zap className="w-4 h-4 text-blue-500" />
                </div>
              )}

              {/* Selected indicator */}
              {(isSelected || isCurrent) && (
                <div className="absolute top-2 right-2">
                  <Check className="w-5 h-5 text-blue-600" />
                </div>
              )}

              {/* Preset name and description */}
              <div className="mb-3">
                <h4 className="font-medium text-gray-900 mb-1">
                  {preset.name}
                </h4>
                {preset.description && (
                  <p className="text-xs text-gray-600 line-clamp-2">
                    {preset.description}
                  </p>
                )}
              </div>

              {/* Parameter summary */}
              <div className="space-y-1 text-xs text-gray-500">
                <div className="flex justify-between">
                  <span>Temperature:</span>
                  <span className="font-medium text-gray-700">
                    {preset.parameters.temperature}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Max Tokens:</span>
                  <span className="font-medium text-gray-700">
                    {preset.parameters.max_tokens?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Top P:</span>
                  <span className="font-medium text-gray-700">
                    {preset.parameters.top_p}
                  </span>
                </div>
              </div>

              {/* Use case badge */}
              {preset.use_case && (
                <div className="mt-3">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    {preset.use_case}
                  </span>
                </div>
              )}

              {/* Delete button for custom presets */}
              {!preset.is_system_preset && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePreset(preset.id, preset.is_system_preset);
                  }}
                  className="absolute bottom-2 right-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                  title="Delete preset"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </button>
          );
        })}
      </div>

      {presets.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Zap className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>No presets available</p>
        </div>
      )}
    </div>
  );
};

export default ParameterPresetSelector;
